import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Refresh,
  Schedule,
  Timer,
  TrendingUp,
  TrendingDown,
  PlayArrow,
  InfoOutlined,
} from '@mui/icons-material';
import { jobService, type JobMetrics, type HealthStatus } from '../services/jobService';
import Button from '@mui/material/Button';

const JobMonitoring: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [jobMetrics, setJobMetrics] = useState<Record<string, JobMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [triggeringJob, setTriggeringJob] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();

    if (autoRefresh) {
      const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [health, metrics] = await Promise.all([
        jobService.getHealthStatus(),
        jobService.getAllJobMetrics(),
      ]);
      setHealthStatus(health);
      setJobMetrics(metrics);
    } catch (err) {
      setError('Failed to load job monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'HEALTHY':
        return 'success';
      case 'WARNING':
        return 'warning';
      case 'CRITICAL':
        return 'error';
      default:
        return 'default';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return <CheckCircle color="success" />;
      case 'WARNING':
        return <Warning color="warning" />;
      case 'CRITICAL':
        return <Error color="error" />;
      default:
        return <Warning />;
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const getSuccessRate = (job: JobMetrics) => {
    if (job.executionCount === 0) return '0.0';
    return ((job.successCount / job.executionCount) * 100).toFixed(1);
  };

  const getJobStatus = (job: JobMetrics) => {
    if (job.consecutiveFailures >= 3) return 'critical';
    if (job.consecutiveFailures > 0) return 'warning';
    return 'success';
  };

  const getJobSchedule = (jobName: string) => {
    const schedules: Record<string, string> = {
      TokenRefreshJob: 'Every 45 minutes',
      ExperimentMetricsCollectionJob: 'Daily at 2:00 AM',
      DailyMetricsIngestionJob: 'Daily at 3:00 AM',
      ExperimentCompletionJob: 'Daily at 4:00 AM',
      DataCleanupJob: 'Sundays at 1:00 AM',
      BingMetricsSyncService: 'Daily at 3:00 AM',
      'ScheduledValidationService.daily': 'Daily at 2:00 AM',
      'ScheduledValidationService.weekly': 'Mondays at 3:00 AM',
      ScheduledChangeDetectionService: 'Hourly',
      SnapshotCleanupService: 'Daily at 1:00 AM',
    };
    return schedules[jobName] || 'Unknown';
  };

  const getJobType = (jobName: string) => {
    const jobTypes: Record<string, string> = {
      TokenRefreshJob: 'token-refresh',
      ExperimentMetricsCollectionJob: 'experiment-metrics',
      DailyMetricsIngestionJob: 'daily-metrics',
      ExperimentCompletionJob: 'experiment-completion',
      DataCleanupJob: 'data-cleanup',
      BingMetricsSyncService: 'bing-metrics-sync',
      'ScheduledValidationService.daily': 'validation-daily',
      'ScheduledValidationService.weekly': 'validation-weekly',
      ScheduledChangeDetectionService: 'change-detection',
      SnapshotCleanupService: 'snapshot-cleanup',
    };
    return jobTypes[jobName];
  };

  const handleTriggerJob = async (jobName: string) => {
    const jobType = getJobType(jobName);
    if (!jobType) return;

    setTriggeringJob(jobName);
    setError(null);
    setSuccessMessage(null);

    try {
      await jobService.triggerJob(jobType);
      setSuccessMessage(`${jobName} triggered successfully`);
      setTimeout(() => setSuccessMessage(null), 5000);
      setTimeout(() => loadData(), 2000);
    } catch (err) {
      setError(`Failed to trigger ${jobName}`);
    } finally {
      setTriggeringJob(null);
    }
  };

  if (loading && Object.keys(jobMetrics).length === 0) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading job monitoring data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Job Monitoring Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Monitor and manage background job processing across all data ingestion and analysis tasks.
        </Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2} alignItems="center" mb={3}>
        <Chip
          label={autoRefresh ? 'Auto-refresh: ON' : 'Auto-refresh: OFF'}
          color={autoRefresh ? 'primary' : 'default'}
          onClick={() => setAutoRefresh(!autoRefresh)}
        />
        <Tooltip title="Refresh Now">
          <IconButton onClick={loadData} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      {/* Core API Batch Jobs Description */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={1.5}>
          <InfoOutlined color="info" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Core API Batch Jobs (Java Spring Boot)
            </Typography>
            <Typography variant="body2" paragraph>
              Monitor and manage 10 Java batch jobs that handle data ingestion, OAuth token management,
              experiment tracking, and data cleanup:
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>Token Refresh</strong> - Every 45 minutes - <em>Input: OAuth tokens from 'integrations' table (GSC, Bing, GA4)</em> - Automatically refreshes API access tokens before expiration to maintain uninterrupted data flow from search engines and analytics platforms
              <br />
              • <strong>Daily Metrics Ingestion</strong> - Daily at 3:00 AM - <em>Input: GSC Search Console API, Bing Webmaster Tools API</em> - Fetches yesterday's search performance metrics (queries, clicks, impressions, positions) and stores in 'gsc_daily_metrics' and 'bing_daily_metrics' tables
              <br />
              • <strong>Bing Metrics Sync</strong> - Daily at 3:00 AM - <em>Input: Bing Webmaster Tools API</em> - Dedicated sync service for Bing search data, handles pagination and API rate limits
              <br />
              • <strong>Experiment Metrics Collection</strong> - Daily at 2:00 AM - <em>Input: Active experiments from 'experiments' table + GSC/Bing metrics from API</em> - Collects search performance data for pages in active A/B experiments, stores in 'experiment_metrics' table for statistical analysis
              <br />
              • <strong>Experiment Completion</strong> - Daily at 4:00 AM - <em>Input: Active experiments + collected metrics from 'experiment_metrics'</em> - Checks if experiments have reached statistical significance or duration thresholds, automatically completes experiments and calculates final results
              <br />
              • <strong>Scheduled Change Detection</strong> - Hourly - <em>Input: Ad-hoc validation snapshots from 'validation_snapshots' table</em> - Processes on-demand page validation requests, compares current page state against expected SEO criteria, stores results in 'validation_history'
              <br />
              • <strong>Scheduled Validation (Daily)</strong> - Daily at 2:00 AM - <em>Input: ALL active pages from 'pages' table</em> - Runs comprehensive SEO validation rules (title, meta, headings, schema) on all active pages, identifies issues and stores in 'validation_history'
              <br />
              • <strong>Scheduled Validation (Weekly)</strong> - Mondays at 3:00 AM - <em>Input: ALL active pages + historical validation data</em> - Deep weekly validation including trend analysis, broken link checks, and comprehensive SEO health scoring
              <br />
              • <strong>Snapshot Cleanup</strong> - Daily at 1:00 AM - <em>Input: Old records from 'validation_snapshots' table (retention: 90 days)</em> - Removes expired validation snapshots to maintain database performance
              <br />
              • <strong>Data Cleanup</strong> - Sundays at 1:00 AM - <em>Input: Old records from 'gsc_daily_metrics', 'bing_daily_metrics', 'experiment_metrics' (retention: 18 months)</em> - Removes historical data beyond retention period while preserving aggregated summaries
            </Typography>
          </Box>
        </Box>
      </Paper>
      {/* Manual Trigger Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manual Job Triggers
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'TokenRefreshJob' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('TokenRefreshJob')}
            disabled={triggeringJob === 'TokenRefreshJob'}
          >
            Token Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'DailyMetricsIngestionJob' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('DailyMetricsIngestionJob')}
            disabled={triggeringJob === 'DailyMetricsIngestionJob'}
          >
            Daily Metrics
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'ExperimentMetricsCollectionJob' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('ExperimentMetricsCollectionJob')}
            disabled={triggeringJob === 'ExperimentMetricsCollectionJob'}
          >
            Experiment Metrics
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'ExperimentCompletionJob' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('ExperimentCompletionJob')}
            disabled={triggeringJob === 'ExperimentCompletionJob'}
          >
            Experiment Completion
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'DataCleanupJob' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('DataCleanupJob')}
            disabled={triggeringJob === 'DataCleanupJob'}
          >
            Data Cleanup
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'BingMetricsSyncService' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('BingMetricsSyncService')}
            disabled={triggeringJob === 'BingMetricsSyncService'}
          >
            Bing Metrics Sync
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'ScheduledValidationService.daily' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('ScheduledValidationService.daily')}
            disabled={triggeringJob === 'ScheduledValidationService.daily'}
          >
            Daily Validation
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'ScheduledValidationService.weekly' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('ScheduledValidationService.weekly')}
            disabled={triggeringJob === 'ScheduledValidationService.weekly'}
          >
            Weekly Validation
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'ScheduledChangeDetectionService' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('ScheduledChangeDetectionService')}
            disabled={triggeringJob === 'ScheduledChangeDetectionService'}
          >
            Change Detection
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={triggeringJob === 'SnapshotCleanupService' ? <CircularProgress size={16} /> : <PlayArrow />}
            onClick={() => handleTriggerJob('SnapshotCleanupService')}
            disabled={triggeringJob === 'SnapshotCleanupService'}
          >
            Snapshot Cleanup
          </Button>
        </Box>
      </Paper>

      {/* Health Status Overview */}
      {healthStatus && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={2}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getHealthIcon(healthStatus.status)}
                  <Typography variant="h6">System Health</Typography>
                </Box>
                <Chip label={healthStatus.status} color={getHealthColor(healthStatus.status)} size="small" />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Jobs
                </Typography>
                <Typography variant="h4">{healthStatus.totalJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Executions
                </Typography>
                <Typography variant="h4">{healthStatus.totalExecutions}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={2}>
            <Card sx={{ bgcolor: healthStatus.totalFailures > 0 ? 'error.50' : 'inherit' }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Failures
                </Typography>
                <Typography variant="h4" color={healthStatus.totalFailures > 0 ? 'error' : 'inherit'}>
                  {healthStatus.totalFailures}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={2}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Failure Rate
                </Typography>
                <Typography variant="h4">{(healthStatus.failureRate * 100).toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={2}>
            <Card sx={{ bgcolor: healthStatus.jobsWithFailures > 0 ? 'warning.50' : 'inherit' }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Jobs with Failures
                </Typography>
                <Typography
                  variant="h4"
                  color={healthStatus.jobsWithFailures > 0 ? 'warning.main' : 'inherit'}
                >
                  {healthStatus.jobsWithFailures}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Job Details Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Name</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Executions</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">Last Duration</TableCell>
                <TableCell align="right">Avg Duration</TableCell>
                <TableCell>Last Success</TableCell>
                <TableCell>Last Failure</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(jobMetrics).map((job) => {
                const status = getJobStatus(job);
                const successRate = getSuccessRate(job);
                const avgDuration =
                  job.executionCount > 0 ? job.totalDurationMs / job.executionCount : 0;

                return (
                  <TableRow
                    key={job.jobName}
                    sx={{
                      bgcolor:
                        status === 'critical'
                          ? 'error.50'
                          : status === 'warning'
                          ? 'warning.50'
                          : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {job.jobName.replace('Job', '')}
                      </Typography>
                      {job.consecutiveFailures > 0 && (
                        <Chip
                          label={`${job.consecutiveFailures} consecutive failures`}
                          color="error"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="body2">{getJobSchedule(job.jobName)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {status === 'success' && <CheckCircle color="success" />}
                      {status === 'warning' && <Warning color="warning" />}
                      {status === 'critical' && <Error color="error" />}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{job.executionCount}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {job.successCount}✓ {job.failureCount}✗
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {Number(successRate) >= 95 ? (
                          <TrendingUp fontSize="small" color="success" />
                        ) : (
                          <TrendingDown fontSize="small" color="error" />
                        )}
                        <Typography variant="body2">{successRate}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        <Timer fontSize="small" color="action" />
                        <Typography variant="body2">{formatDuration(job.lastDurationMs)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatDuration(avgDuration)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.75rem">
                        {formatTime(job.lastSuccessTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {job.lastFailureTime ? (
                        <>
                          <Typography variant="body2" fontSize="0.75rem" color="error">
                            {formatTime(job.lastFailureTime)}
                          </Typography>
                          {job.lastError && (
                            <Typography variant="caption" color="error" display="block">
                              {job.lastError.substring(0, 50)}...
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Trigger Job Now">
                        <span>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleTriggerJob(job.jobName)}
                            disabled={triggeringJob === job.jobName}
                          >
                            {triggeringJob === job.jobName ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayArrow />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {Object.keys(jobMetrics).length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No job executions recorded yet. Jobs will appear here after their first run.
        </Alert>
      )}
    </Box>
  );
};

export default JobMonitoring;
